import React from 'react';
import { Helmet } from 'react-helmet-async';

const LAST_UPDATED = '15 November 2025';

const CookiePolicyPage = () => {
  const categories = [
    {
      title: 'Strictly necessary cookies',
      description: 'Essential for logging in, keeping sessions secure, loading dashboards and remembering which tab you opened. These cookies are set automatically and cannot be switched off through the banner.'
    },
    {
      title: 'Functional cookies',
      description: 'Used to remember preferences such as invoice filters, notification drawers and whether you have dismissed certain banners. They help the portal feel consistent across visits.'
    },
    {
      title: 'Analytics cookies (Google Analytics)',
      description: 'Optional cookies that help us understand which marketing pages and knowledge base articles are most useful. They collect aggregated information such as page views, referral sources and device type. They only load after you grant consent and you can withdraw that consent at any time.'
    },
    {
      title: 'Third-party cookies',
      description: 'Stripe may set cookies when you connect an account or complete the hosted onboarding journey. These are required to comply with financial regulations and are covered by Stripe’s own notices.'
    }
  ];

  const managingCookies = [
    'Use the cookie banner to accept or decline non-essential cookies.',
    'Adjust settings in the Receevit® client portal under Settings → Privacy if you change your mind later.',
    'Manage cookies in your browser (Chrome, Edge, Safari, Firefox and others provide instructions under their privacy menus).',
    'Opt out of Stripe or MailerSend cookies via the respective preferences presented during their hosted flows.'
  ];

  return (
    <div className="min-h-screen bg-[#1F11ED] px-4 sm:px-6 lg:px-8">
      <Helmet>
        <title>Cookie Policy | Receevit®</title>
        <meta
          name="description"
          content="Understand how Receevit® uses cookies and similar technologies across receevit.com and the client portal."
        />
        <link rel="canonical" href="https://www.receevit.com/cookies" />
        <meta property="og:title" content="Cookie Policy | Receevit®" />
        <meta property="og:description" content="Understand how Receevit® uses cookies and similar technologies across receevit.com and the client portal." />
        <meta property="og:url" content="https://www.receevit.com/cookies" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.receevit.com/og-image.png" />
      </Helmet>

      <div className="max-w-3xl mx-auto text-white py-24">
        <div className="bg-white/10 rounded-3xl p-10 mb-10">
          <p className="text-sm uppercase tracking-widest text-white/80">Cookie Policy</p>
          <h1 className="text-4xl font-bold mt-4 mb-4">How Receevit® uses cookies</h1>
          <p className="text-white/90 text-lg">
            Cookies help keep your session secure, remember where you left off and minimise repeated logins. We keep their use proportionate and transparent.
          </p>
          <div className="mt-6 p-4 bg-white/10 rounded-2xl text-sm text-white">
            <strong>Last updated:</strong> {LAST_UPDATED}
          </div>
        </div>

        <section className="space-y-6">
          <p className="text-white/85 text-lg leading-relaxed">
            This Cookie Policy explains how Receevit Ltd (“Receevit®”, “we”, “us”) uses cookies and similar technologies on receevit.com and within the Receevit® client portal.
            It supplements our Privacy Policy and applies to visitors, triallists and paying customers alike. Optional analytics cookies only load after you accept them in the banner or the “Cookie Preferences” link in our footer.
          </p>

          <div className="space-y-5">
            {categories.map((category) => (
              <div key={category.title} className="p-5 bg-white/5 rounded-3xl text-white">
                <h2 className="text-2xl font-semibold mb-2">{category.title}</h2>
                <p className="text-white/85">{category.description}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-10">
          <h2 className="text-2xl font-semibold text-white mb-4">Cookie duration</h2>
          <p className="text-white/85 leading-relaxed">
            Session cookies expire when you log out or close your browser. Preference cookies usually last up to six months so we can respect your selections, while analytics cookies (when enabled) expire within thirteen months. Stripe’s onboarding cookies follow their own retention schedule, which is documented in Stripe’s notices.
          </p>
        </section>

        <section className="mt-10">
          <h2 className="text-2xl font-semibold text-white mb-4">Managing your cookies</h2>
          <ul className="space-y-3 text-white/85">
            {managingCookies.map((tip) => (
              <li key={tip} className="flex items-start space-x-3">
                <span className="mt-1 h-2 w-2 rounded-full bg-white"></span>
                <span>{tip}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-10">
          <h2 className="text-2xl font-semibold text-white mb-4">Updates</h2>
          <p className="text-white/85">
            We review this Cookie Policy whenever new technologies are introduced or when regulations change. We will highlight material updates through the cookie banner or in-app notifications. You can revisit your choice at any time by selecting “Cookie Preferences” in the site footer.
          </p>
        </section>
      </div>
    </div>
  );
};

export default CookiePolicyPage;

